import moment from 'moment';
import React from 'react';
import { faCopy } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ServicesContext from '../../contexts/ServicesContext';
import { useAuth } from '../../contexts/AuthContext';
import { strings } from '../../resources/strings';
import { orderTags } from '../../resources/orderResources';
import { OrderAlertType } from '../../enums/OrderAlertType';
import { FramesQuantity } from '../../enums/FramesQuantity';
import { IFilterToListOrdersOutput, getAppliedStatePrescriptionIds } from '../ModalFiltersToListOrders/ModalFiltersToListOrders';
import { Container, Filter, FilterName, FilterValue, Title, Filters, CopyButton } from './styles';
import { omnichannelResources } from '../../resources/omnichannelResources';

interface IFiltersProps {
  appliedFilters?: IFilterToListOrdersOutput;
  marginTop?: string;
}

const AppliedFiltersToListOrders: React.FC<IFiltersProps> = ({
  appliedFilters,
  marginTop
}) => {

  const {
    statesService,
    statesPrescriptionsService,
    toastService
  } = React.useContext(ServicesContext);

  const { session } = useAuth();

  const anyAppliedFilter =
    appliedFilters?.numbers ||
    appliedFilters?.startDate ||
    appliedFilters?.endDate ||
    (appliedFilters?.stateIds && appliedFilters?.stateIds) ||
    getAppliedStatePrescriptionIds(appliedFilters).length > 0 ||
    appliedFilters?.customerName ||
    appliedFilters?.customerEmail ||
    appliedFilters?.specialTag ||
    (appliedFilters?.orderAlertType && appliedFilters?.orderAlertType > 0) ||
    (appliedFilters?.locationId && appliedFilters?.locationId > 0) ||
    (appliedFilters?.sellerId && appliedFilters?.sellerId > 0) ||
    (appliedFilters?.stateIdsForUserProfile && appliedFilters?.stateIdsForUserProfile) ||
    appliedFilters?.deliveryDeadlineStartDate ||
    appliedFilters?.deliveryDeadlineEndDate ||
    appliedFilters?.deliveryStartDate ||
    appliedFilters?.deliveryEndDate ||
    appliedFilters?.lensMaterialName ||
    appliedFilters?.lensClassName ||
    appliedFilters?.fulfilledBy ||
    appliedFilters?.originedBy ||
    appliedFilters?.ignoreDefaultUserProfileFilters ||
    appliedFilters?.salesTypeFilter ||
    (appliedFilters?.framesQuantity && appliedFilters?.framesQuantity > 0);

  const renderFilterNumbers = () => {
    if (!appliedFilters?.numbers) return null;

    let numbers = appliedFilters.numbers;
    if (numbers.length > 20) numbers = numbers.substring(0, 19) + '...';

    return (
      <Filter>
        <FilterName>{strings.Orders}: </FilterName>
        <FilterValue>{numbers}</FilterValue>
        <CopyButton title='Copiar' onClick={() => copyNumbersToClipboard()}><FontAwesomeIcon icon={faCopy} color='gray' size='sm' /></CopyButton>
      </Filter>
    );
  }

  const renderFilterDates = () => {
    if (!appliedFilters?.startDate && !appliedFilters?.endDate) return null;

    let value = '';
    value += appliedFilters?.startDate ? 'de ' + moment(appliedFilters?.startDate).format('DD/MM/yyyy') : '';
    value += appliedFilters?.endDate ? ' até ' + moment(appliedFilters?.endDate).format('DD/MM/yyyy') : '';

    return (
      <Filter>
        <FilterName>{strings.Paid}: </FilterName>
        <FilterValue>{value}</FilterValue>
      </Filter>
    );
  }

  const renderFilterDeliveryDeadlineDates = () => {
    if (!appliedFilters?.deliveryDeadlineStartDate && !appliedFilters?.deliveryDeadlineEndDate) return null;

    let value = '';
    value += appliedFilters?.deliveryDeadlineStartDate ? 'de ' + moment(appliedFilters?.deliveryDeadlineStartDate).format('DD/MM/yyyy') : '';
    value += appliedFilters?.deliveryDeadlineEndDate ? ' até ' + moment(appliedFilters?.deliveryDeadlineEndDate).format('DD/MM/yyyy') : '';

    return (
      <Filter>
        <FilterName>{strings.CustomerDeadline}: </FilterName>
        <FilterValue>{value}</FilterValue>
      </Filter>
    );
  }

  const renderFilterDeliveryDates = () => {
    if (!appliedFilters?.deliveryStartDate && !appliedFilters?.deliveryEndDate) return null;

    let value = '';
    value += appliedFilters?.deliveryStartDate ? 'de ' + moment(appliedFilters?.deliveryStartDate).format('DD/MM/yyyy') : '';
    value += appliedFilters?.deliveryEndDate ? ' até ' + moment(appliedFilters?.deliveryEndDate).format('DD/MM/yyyy') : '';

    return (
      <Filter>
        <FilterName>{strings.Delivered}: </FilterName>
        <FilterValue>{value}</FilterValue>
      </Filter>
    );
  }

  const renderCustomerName = () => {
    if (!appliedFilters?.customerName) return null;

    return (
      <Filter>
        <FilterName>{strings.Client}: </FilterName>
        <FilterValue>{appliedFilters?.customerName}</FilterValue>
      </Filter>
    );
  }

  const renderCustomerEmail = () => {
    if (!appliedFilters?.customerEmail) return null;

    return (
      <Filter>
        <FilterName>{strings.Email}: </FilterName>
        <FilterValue>{appliedFilters?.customerEmail}</FilterValue>
      </Filter>
    );
  }

  const renderFilterOrderState = () => {
    if (!appliedFilters?.stateIds || +appliedFilters?.stateIds.split(' ')[0] <= 0) return null;

    const stateId = +appliedFilters.stateIds.split(' ')[0];

    const states = statesService.getTranslateByUserProfile(session?.user?.profileType);
    const state = states.find(x => x.id === stateId)?.name;

    const deliveryStates = statesService.getDeliveryTranslateByUserProfile(session?.user?.profileType);
    const deliveryState = deliveryStates.find(x => x.id === stateId)?.name;

    return (
      <Filter>
        <FilterName>{strings.StateOfOrder}: </FilterName>
        <FilterValue>{state || deliveryState}</FilterValue>
      </Filter>
    );
  }

  const renderFilterPrecriptionState = () => {
    const stateIds = getAppliedStatePrescriptionIds(appliedFilters);
    if (stateIds.length <= 0) return null;

    const states = statesPrescriptionsService.get();
    const state = stateIds
      .map(stateId => states.find(x => x.id === +stateId)?.description)
      .filter(description => !!description)
      .join(', ');

    return (
      <Filter>
        <FilterName>{strings.StateOfDegree}: </FilterName>
        <FilterValue>{state}</FilterValue>
      </Filter>
    );
  }

  const renderSpecialTag = () => {
    if (!appliedFilters?.specialTag) return null;

    let filterValue = '';
    switch (appliedFilters?.specialTag) {
      case orderTags.amaro.value:
        filterValue = orderTags.amaro.display;
        break;
      case orderTags.influenciadores.value:
        filterValue = orderTags.influenciadores.display;
        break;
      case orderTags.non_progressive_order_bf2023.value:
        filterValue = orderTags.non_progressive_order_bf2023.display;
        break;
      case orderTags.externally_changed.value:
        filterValue = orderTags.externally_changed.display;
        break;
      case orderTags.pre_sale_sport.value:
        filterValue = orderTags.pre_sale_sport.display;
        break;
    }

    return (
      <Filter>
        <FilterName>{strings.SpecialTag}: </FilterName>
        <FilterValue>{filterValue || appliedFilters?.specialTag}</FilterValue>
      </Filter>
    );
  }

  const renderOrderAlertType = () => {
    if (!appliedFilters?.orderAlertType) return null;

    let filterValue = '';

    switch (+appliedFilters.orderAlertType) {
      case OrderAlertType.NoCustumer:
        filterValue = strings.CustomerNotInformed;
        break;
      case OrderAlertType.OrderIsLate:
        filterValue = strings.InternalDeadlineExpired;
        break;
      case OrderAlertType.NoShippingLine:
        filterValue = strings.OrderWithoutShippingLine;
        break;
      default:
        return null;
    }

    return (
      <Filter>
        <FilterName>{strings.AlertType}: </FilterName>
        <FilterValue>{filterValue}</FilterValue>
      </Filter>
    );
  }

  const renderFilterLocation = () => {
    if (!appliedFilters?.locationId || appliedFilters?.locationId <= 0) return null;

    return (
      <Filter>
        <FilterName>{strings.Location}: </FilterName>
        <FilterValue>{appliedFilters?.locationName}</FilterValue>
      </Filter>
    );
  }

  const renderSellerName = () => {
    if (!appliedFilters?.sellerName) return null;

    return (
      <Filter>
        <FilterName>{strings.Seller}: </FilterName>
        <FilterValue>{appliedFilters?.sellerName}</FilterValue>
      </Filter>
    );
  }

  const renderLensClassName = () => {
    if (!appliedFilters?.lensClassName) return null;

    return (
      <Filter>
        <FilterName>{strings.LensMaterial}: </FilterName>
        <FilterValue>{appliedFilters?.lensClassName}</FilterValue>
      </Filter>
    );
  }

  const renderFilterFulfilledBy = () => {
    if (!appliedFilters?.fulfilledBy) return null;

    return (
      <Filter>
        <FilterName>{strings.FulfilledBy}: </FilterName>
        <FilterValue>{omnichannelResources.FulfillmentOptions.find(f => f.filterId == appliedFilters?.fulfilledBy)?.title}</FilterValue>
      </Filter>
    );
  }

  const renderFilterOriginedBy = () => {
    if (!appliedFilters?.originedBy) return null;

    return (
      <Filter>
        <FilterName>{strings.OriginatedBy}: </FilterName>
        <FilterValue>{omnichannelResources.OrderOriginOptions.find(f => f.filterId == appliedFilters?.originedBy)?.title}</FilterValue>
      </Filter>
    );
  }

  const renderFilterIgnoreDefaultUserProfileFilters = () => {
    if (!appliedFilters?.ignoreDefaultUserProfileFilters) return null;

    return (
      <Filter>
        <FilterName>Filtros padrões perfil: </FilterName>
        <FilterValue>{appliedFilters?.ignoreDefaultUserProfileFilters ? strings.Not : strings.Yes}</FilterValue>
      </Filter>
    );
  }

  const renderFilterSalesType = () => {
    if (!appliedFilters?.salesTypeFilter) return null;

    const filterValue = appliedFilters.salesTypeFilter === 'pre-sale' ? strings.PreSale : strings.Sale;

    return (
      <Filter>
        <FilterName>{strings.SalesType}: </FilterName>
        <FilterValue>{filterValue}</FilterValue>
      </Filter>
    );
  }

  const renderFilterFramesQuantity = () => {
    if (!appliedFilters?.framesQuantity) return null;

    let filterValue = '';

    switch (+appliedFilters.framesQuantity) {
      case FramesQuantity.ManyOnly:
        filterValue = strings.ManyFramesOnly;
        break;
      case FramesQuantity.OneOnly:
        filterValue = strings.OneFrameOnly;
        break;
      default:
        return null;
    }

    return (
      <Filter>
        <FilterName>{strings.FramesQuantity}: </FilterName>
        <FilterValue>{filterValue}</FilterValue>
      </Filter>
    );
  }

  if (!anyAppliedFilter) {
    return (
      <Container marginTop={marginTop}>
        <Title>{strings.AppliedFilters}</Title>
        <Filters>{strings.None}</Filters>
      </Container>
    );
  }

  return (
    <Container marginTop={marginTop}>
      <Title>{strings.AppliedFilters}</Title>
      <Filters>
        {renderFilterNumbers()}
        {renderFilterLocation()}
        {renderFilterDates()}
        {renderFilterDeliveryDeadlineDates()}
        {renderFilterDeliveryDates()}
        {renderFilterOrderState()}
        {renderFilterPrecriptionState()}
        {renderCustomerName()}
        {renderCustomerEmail()}
        {renderSpecialTag()}
        {renderOrderAlertType()}
        {renderSellerName()}
        {renderLensClassName()}
        {renderFilterFulfilledBy()}
        {renderFilterOriginedBy()}
        {renderFilterIgnoreDefaultUserProfileFilters()}
        {renderFilterSalesType()}
        {renderFilterFramesQuantity()}
      </Filters>
    </Container>
  );

  function copyNumbersToClipboard() {
    const text = appliedFilters?.numbers || '';
    try {
      navigator.clipboard.writeText(text);
      toastService.success('Os números dos pedidos foram copiados com sucesso. Agora você pode usar o Ctrl+V para colar em outro lugar!');
    } catch {
      toastService.error('Não foi possível copiar os números dos pedidos. Tente novamente!');
    }
  }
}

export { AppliedFiltersToListOrders };